/**
 * Console shell: a deep-forest navigation rail plus a cream working area.
 *
 * Navigation entries are filtered by capability, so a support account never sees
 * a link it cannot use. That is presentation only — see the security note in
 * `src/lib/accounts.ts`.
 */

import { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  BadgePercent,
  Bell,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  GalleryHorizontal,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  MapPinned,
  Menu,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  UserSquare,
  Warehouse,
  X,
} from 'lucide-react';
import { can, ROLE_LABELS, type Capability } from '../lib/accounts';
import { isOpen } from '../lib/applications';
import { useAuth, useCurrentUser } from '../store/auth';
import { useDb } from '../store/db';
import { useUi } from '../store/ui';
import { brandMark, brandName, wordmarkLight } from '../lib/assets';
import { cn } from '../lib/format';
import { Toaster } from './ui';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof LayoutDashboard;
  capability: Capability;
  /** Renders a live count pill, e.g. the moderation queue. */
  badge?: 'pendingBookings' | 'pendingReviews' | 'openApplications';
}

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', Icon: LayoutDashboard, capability: 'dashboard.view' },
    ],
  },
  {
    group: 'Operations',
    items: [
      {
        to: '/bookings',
        label: 'Bookings',
        Icon: CalendarDays,
        capability: 'bookings.view',
        badge: 'pendingBookings',
      },
      { to: '/therapists', label: 'Therapists', Icon: UserSquare, capability: 'therapists.view' },
      {
        to: '/applications',
        label: 'Applications',
        Icon: ClipboardList,
        capability: 'applications.view',
        badge: 'openApplications',
      },
      // Partners' withdrawals and wallets: the finance queue.
      { to: '/payouts', label: 'Payouts', Icon: Landmark, capability: 'payouts.view' },
      { to: '/customers', label: 'Customers', Icon: Users, capability: 'customers.view' },
      // Kits, uniforms and equipment: the counter, the warehouse and every bag.
      { to: '/production', label: 'Production', Icon: Warehouse, capability: 'production.view' },
    ],
  },
  {
    group: 'Catalogue',
    items: [
      { to: '/packages', label: 'Packages', Icon: Sparkles, capability: 'catalogue.view' },
      { to: '/categories', label: 'Categories', Icon: LayoutGrid, capability: 'categories.view' },
    ],
  },
  {
    group: 'Growth',
    items: [
      { to: '/offers', label: 'Offers & coupons', Icon: BadgePercent, capability: 'offers.view' },
      {
        to: '/reviews',
        label: 'Reviews',
        Icon: Star,
        capability: 'reviews.view',
        badge: 'pendingReviews',
      },
      { to: '/notifications', label: 'Notifications', Icon: Bell, capability: 'notifications.view' },
      // The website hero and the home carousel on the website and the app.
      { to: '/banners', label: 'Banners', Icon: GalleryHorizontal, capability: 'banners.view' },
    ],
  },
  {
    group: 'Configuration',
    items: [
      // Where bookings and partner sign-ups are taken: states, cities, radii.
      { to: '/locations', label: 'Locations', Icon: MapPinned, capability: 'locations.view' },
      { to: '/settings', label: 'Settings', Icon: Settings, capability: 'settings.view' },
      // Console logins and the pages each may open. Administrators only.
      { to: '/employees', label: 'Employees', Icon: ShieldCheck, capability: 'staff.manage' },
    ],
  },
];

export default function AdminLayout() {
  const user = useCurrentUser();
  const signOut = useAuth((s) => s.signOut);
  const navOpen = useUi((s) => s.navOpen);
  const setNavOpen = useUi((s) => s.setNavOpen);
  const location = useLocation();

  const pendingBookings = useDb((s) => s.bookings.filter((b) => b.status === 'Pending').length);
  const pendingReviews = useDb((s) => s.reviews.filter((r) => r.state === 'pending').length);
  const openApplications = useDb((s) => s.applications.filter((a) => isOpen(a.status)).length);
  const acceptingBookings = useDb((s) => s.settings.acceptingBookings);

  const counts = { pendingBookings, pendingReviews, openApplications };

  // Close the drawer on navigation, or it stays open over the new page.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, setNavOpen]);

  const groups = NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => can(user, item.capability)),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[var(--rail-width)_1fr]">
      {/* Drawer scrim, small screens only */}
      {navOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="animate-fade-in fixed inset-0 z-40 cursor-default bg-[#0a241d]/50 lg:hidden"
        />
      ) : null}

      {/* Rail */}
      <aside
        className={cn(
          'rail fixed inset-y-0 left-0 z-50 flex w-[var(--rail-width)] flex-col bg-[#0a241d] transition-transform duration-250',
          'lg:sticky lg:top-0 lg:h-dvh lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between gap-2 px-5 py-5">
          <span className="flex min-w-0 items-center gap-2.5">
            <img src={brandMark} alt="" aria-hidden className="h-[30px] w-auto" />
            <span aria-hidden className="h-6 w-px bg-white/15" />
            <img src={wordmarkLight} alt={brandName} className="h-[30px] w-auto" />
          </span>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="inline-flex size-8 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 lg:hidden"
          >
            <X size={17} strokeWidth={2.2} aria-hidden />
          </button>
        </div>

        <p className="px-5 pb-4 text-[10px] font-bold tracking-[0.15em] text-[#c69b3f] uppercase">
          Operations console
        </p>

        <nav aria-label="Console sections" className="scroll-slim min-h-0 flex-1 overflow-y-auto px-3 pb-4">
          {groups.map((group) => (
            <div key={group.group} className="mb-5">
              <p className="mb-1.5 px-2 text-[10px] font-bold tracking-[0.12em] text-white/38 uppercase">
                {group.group}
              </p>
              <ul className="flex flex-col gap-0.5">
                {group.items.map(({ to, label, Icon, badge }) => {
                  const count = badge ? counts[badge] : 0;

                  return (
                    <li key={to}>
                      <NavLink
                        to={to}
                        end={to === '/'}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-[13.5px] font-semibold no-underline transition-colors',
                            isActive
                              ? 'bg-white/12 text-white'
                              : 'text-white/64 hover:bg-white/6 hover:text-white',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon
                              size={17}
                              strokeWidth={isActive ? 2.3 : 1.9}
                              aria-hidden
                              className="shrink-0"
                            />
                            <span className="min-w-0 flex-1 truncate">{label}</span>
                            {count > 0 ? (
                              <span className="inline-flex min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[#c69b3f] px-1.5 py-0.5 text-[10px] font-bold text-[#0a241d]">
                                {count}
                              </span>
                            ) : null}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Account */}
        <div className="border-t border-white/10 p-3">
          {!acceptingBookings ? (
            <p className="mb-2.5 rounded-xl bg-[#b4453a]/20 px-3 py-2.5 text-[11.5px] leading-relaxed font-semibold text-[#f0b8b1]">
              {can(user, 'settings.edit')
                ? 'Bookings are paused site-wide. Re-enable in Settings.'
                : 'Bookings are paused site-wide.'}
            </p>
          ) : null}

          <div className="flex items-center gap-2.5 rounded-xl bg-white/6 px-3 py-2.5">
            <span
              aria-hidden
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-[#c69b3f] text-[12px] font-bold text-[#0a241d]"
            >
              {(user?.name ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-bold text-white">
                {user?.name}
              </span>
              <span className="block truncate text-[10.5px] text-white/55">
                {user?.jobTitle || (user ? ROLE_LABELS[user.role] : '')}
                {user?.city ? ` · ${user.city}` : ' · All cities'}
              </span>
            </span>
            <button
              type="button"
              onClick={() => {
                void signOut().then(() => useDb.getState().clear());
              }}
              title="Sign out"
              aria-label="Sign out"
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut size={15} strokeWidth={2.1} aria-hidden />
            </button>
          </div>
        </div>
      </aside>

      {/* Working area */}
      <div className="flex min-w-0 flex-col">
        {/* Topbar, small screens only */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[#e6e2da] bg-[#f6f4f0]/94 px-4 py-3 backdrop-blur-md lg:hidden">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            className="inline-flex size-9 items-center justify-center rounded-xl border border-[#e6e2da] bg-white text-[#12241e]"
          >
            <Menu size={17} strokeWidth={2.2} aria-hidden />
          </button>

          <span className="flex min-w-0 items-center gap-2">
            <img src={brandMark} alt={brandName} className="h-7 w-auto" />
            <span className="truncate text-[13px] font-bold text-[#12241e]">
              Operations console
            </span>
          </span>

          {pendingBookings > 0 && can(user, 'bookings.view') ? (
            <NavLink
              to="/bookings"
              className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-[#f8eeda] px-2.5 py-1 text-[11.5px] font-bold text-[#b0781f] no-underline"
            >
              {pendingBookings} pending
              <ChevronRight size={12} strokeWidth={2.4} aria-hidden />
            </NavLink>
          ) : null}
        </header>

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
          <Outlet />
        </main>
      </div>

      <Toaster />
    </div>
  );
}
